import moment, { Moment } from "moment"
import { createSelector } from "reselect"
import { get } from "lodash"
import { ReduxState } from "model/state"
import { DATE_FORMAT, TIME_FORMAT_TZ } from "./dates"
import { Uuid } from "./types/uuid"

export function generateIntradayBatchUrlAt(accountAlias: string, portfolioId: string | Uuid, asOf: string) {
  return `/api/${accountAlias}/p/${portfolioId}/intraday/batches/last-completed?asOf=${encodeURIComponent(asOf)}`
}

const getIntradayCurrentDataIdx = (state: ReduxState): number | null =>
  get(state, "global.intraday.refresh.currentDataIdx", null)

// Returns the instant captured when currentDataIdx last changed — frozen
// until the next tick (socket pulse / manual refresh) so all consumers
// share the same asOf and hit one cache entry. Without this, per-hook
// moment() calls would each generate a unique URL.
export const getIntradayBatchQueryAsOf = createSelector(
  [getIntradayCurrentDataIdx],
  () => moment().format(),
)

export type IntradayRelativeDay = "today" | "yesterday" | `${number} days ago`

export interface IntradayAsOfDisplay {
  currentAsOf: string
  intradayRelativeDay: IntradayRelativeDay
}

export function computeIntradayAsOfDisplay(
  intradayBatchMetadata: { asOf?: string, date?: string } | null | undefined,
  now: Moment = moment(),
): IntradayAsOfDisplay {
  if (!intradayBatchMetadata?.asOf) {
    return { currentAsOf: "-", intradayRelativeDay: "today" }
  }
  const { asOf, date } = intradayBatchMetadata
  if (!date) {
    return { currentAsOf: moment(asOf, TIME_FORMAT_TZ).format("LTS"), intradayRelativeDay: "today" }
  }
  const batchMoment = moment(`${date} ${asOf}`, `${DATE_FORMAT} ${TIME_FORMAT_TZ}`)
  if (!batchMoment.isValid()) return { currentAsOf: "-", intradayRelativeDay: "today" }
  // Compare batch and now as calendar dates in the client's local tz
  const dayDiff = moment(now.format(DATE_FORMAT), DATE_FORMAT)
    .diff(moment(batchMoment.format(DATE_FORMAT), DATE_FORMAT), "days")
  return {
    currentAsOf: batchMoment.format("LTS"),
    intradayRelativeDay: relativeDayLabel(dayDiff),
  }
}

function relativeDayLabel(dayDiff: number): IntradayRelativeDay {
  if (dayDiff <= 0) return "today"
  if (dayDiff === 1) return "yesterday"
  return `${dayDiff} days ago`
}
